import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import lodashDebounce from 'lodash/debounce';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../../../Components/Button';
import List from '../../../../Components/List';
import { ScaleButtonAnimation } from '../../../../Components/Web/Intereaction';
import constants from '../../../../Config/constants';
import { getReservationStoreParams } from '../../../../Helper';
import useFavorite from '../../../../Hooks/useFavorite';
import routeList, { ReservationNavigatorProps } from '../../../../Routes/list';
import { EventDetails } from '../../../../RTK/api/events/types';
import { Listing, TableDetails } from '../../../../RTK/api/reservations/types';
import { StoreDetails } from '../../../../RTK/api/store/types';
import { changeReservationStoreFilter } from '../../../../RTK/filter';
import themeStyle from '../../../../Theme/styles';

import Arrow, { LeftRight } from './Arrow';
import Base from './Base';
import Event from './Event';
import CardLoader from './Loader';
import Table from './Table';

export type Query = {
  date: string;
  event_date_range: number;
  is_event: boolean;
  party_size: number;
  time: string;
};

export type CardProps = Listing & {
  loading?: boolean;
  params?: Query;
  onBookNow?: (
    storeInfo: StoreDetails,
    cardData: TableDetails,
    timeSlot: string,
  ) => void;
  onNavigate?: () => void;
};

const ClickableComponent = constants.isWeb ? ScaleButtonAnimation : Button;

const ReservationCard = (props: CardProps) => {
  const dispatch = useDispatch();
  const flatListRef = useRef<List>();
  const navigation = useNavigation<NavigationProp<ReservationNavigatorProps>>();
  const { isSaved, toggleFavourite } = useFavorite(props?.store?.id);
  const [cardWidth, setCardWidth] = useState(0);
  const [scrollIndex, setScrollIndex] = useState(0);

  const {
    events = [],
    loading,
    params,
    store,
    tables = [],
    onBookNow,
    onNavigate,
  } = props;

  // _debouncedOnLayout and _handleCardSizeChange handle onLayout event of card for setting the cardWidth
  const _handleCardSizeChange = useCallback(
    (width: number) => setCardWidth(width),
    [],
  );
  const _debouncedOnLayout = useRef(
    lodashDebounce(_handleCardSizeChange, 300),
  ).current;

  const items = useMemo(() => {
    if (loading) return [];
    return [
      ...events.map((item) => ({
        ...item,
        type: constants.RESERVATION_TYPE.EVENT,
      })),
      ...tables.map((item) => ({
        ...item,
        type: constants.RESERVATION_TYPE.DINING,
      })),
    ];
  }, [events, loading, tables]);

  const eventOrDining = useMemo(
    () =>
      params?.is_event
        ? constants.RESERVATION_TYPE.EVENT
        : lodashIsEmpty(events)
        ? constants.RESERVATION_TYPE.DINING
        : constants.RESERVATION_TYPE.EVENT,
    [events, params?.is_event],
  );

  const storeParams = useMemo(
    () =>
      getReservationStoreParams(
        { params: { ...params, storeId: store?.id } },
        eventOrDining,
      ) as ReservationNavigatorProps[routeList.RES_STORE_DETAILS],
    [eventOrDining, params, store?.id],
  );

  if (loading) return <CardLoader />;

  const _goToStore = () => {
    onNavigate?.();
    navigation.navigate(routeList.RES_STORE_DETAILS, storeParams);
  };

  const _goToDetails = (data: EventDetails) => {
    dispatch(
      changeReservationStoreFilter({
        eventDate: storeParams.eventDateRange,
        eventDateRange: storeParams.eventDateRange
          ? Number(storeParams.eventDateRange)
          : undefined,
        tableDate: storeParams.date,
        tableTime: storeParams.time,
        partySize: storeParams.partySize ? Number(storeParams.partySize) : 2,
      }),
    );
    onNavigate?.();
    navigation.navigate(routeList.RES_EVENT_DETAILS, {
      eventId: data.id,
      size: params?.party_size,
      selectedDate: params?.is_event ? undefined : storeParams.date,
    });
  };

  const _scrollTo = (direction: LeftRight) => {
    const newIndex = scrollIndex + (direction === LeftRight.LEFT ? -1 : 1);
    flatListRef.current._scrollToIndex({
      animated: true,
      index: newIndex,
      viewPosition: 0.5,
    });
    setScrollIndex(newIndex);
  };

  return (
    // @ts-ignore: ClickableComponent is both js file for mobile and web
    <ClickableComponent
      style={themeStyle.flex1}
      onPress={_goToStore}
      onLayout={(event: LayoutChangeEvent) =>
        _debouncedOnLayout(event?.nativeEvent?.layout?.width)
      }
      plain
    >
      {/* Base Card */}
      <Base {...store} isSaved={isSaved} toggleBookmark={toggleFavourite} />
      {/* Card Footer */}
      {Boolean(cardWidth) && (
        <View style={themeStyle.flex1}>
          {/* Arrow Left */}
          {scrollIndex > 0 && (
            <Arrow
              position={LeftRight.LEFT}
              onPress={() => _scrollTo(LeftRight.LEFT)}
            />
          )}
          {/* Card Footer List */}
          <List
            ref={flatListRef}
            data={items}
            snapToInterval={cardWidth - StyleSheet.hairlineWidth}
            snapToAlignment="center"
            decelerationRate="fast"
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <View style={{ width: cardWidth - StyleSheet.hairlineWidth }}>
                {item.type === constants.RESERVATION_TYPE.EVENT ? (
                  <Event {...item} onPress={() => _goToDetails(item)} />
                ) : (
                  <Table
                    {...item}
                    onPress={(ts: string) => onBookNow?.(store, item, ts)}
                  />
                )}
              </View>
            )}
            horizontal
            plain
          />
          {/* Arrow Right */}
          {scrollIndex < items.length - 1 && (
            <Arrow
              position={LeftRight.RIGHT}
              onPress={() => _scrollTo(LeftRight.RIGHT)}
            />
          )}
        </View>
      )}
    </ClickableComponent>
  );
};

export default memo(ReservationCard);
